import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, Play, Radio, Sparkles, Timer } from "lucide-react";
import { api, fmtVal } from "@/lib/api";
import { RichText } from "@/components/meridian/RichText";

function LiveAI({ ai }) {
  return (
    <div className="panel p-6 mt-6" data-testid="presenter-ai">
      <div className="flex items-center justify-between mb-3">
        <span className="eyebrow flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-sky-600" />Live AI response</span>
        {ai && !ai.done && <span className="flex items-center gap-1.5 text-[11px] text-red-600" data-testid="presenter-ai-live"><Radio className="w-3.5 h-3.5 animate-pulse" />Streaming</span>}
      </div>
      {!ai ? <div className="text-sm text-slate-500">AI answers on the main screen will appear here as they stream.</div> : (
        <>
          <div className="text-sm text-slate-500 italic mb-2" data-testid="presenter-ai-question">"{ai.question}"</div>
          <div className="max-h-72 overflow-y-auto thin-scroll text-slate-800" data-testid="presenter-ai-text">{ai.text ? <RichText text={ai.text} /> : <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}</div>
        </>
      )}
    </div>
  );
}

function Fig({ label, value, id, tone }) {
  return <div className="flex justify-between items-baseline py-1.5 border-t border-slate-100 first:border-t-0"><span className="text-xs text-slate-500">{label}</span><span className={`num text-lg ${tone || "text-slate-900"}`} data-testid={`presenter-fig-${id}`}>{value}</span></div>;
}

function KeyFigures({ f }) {
  if (!f) return null;
  const l = f.loan;
  return (
    <div className="panel p-5" data-testid="presenter-figures">
      <div className="eyebrow mb-2">Key figures · live</div>
      {l && <>
        <div className="text-[11px] text-slate-500 mb-1">{l.name}</div>
        <Fig id="dscr" label="DSCR" value={fmtVal(l.dscr, "x")} tone={l.dscr != null && l.dscr < 1.25 ? "text-red-600" : undefined} />
        <Fig id="ltv" label="LTV" value={fmtVal(l.ltv, "pct")} tone={l.ltv != null && l.ltv > 0.65 ? "text-amber-700" : undefined} />
        <Fig id="noi" label="NOI" value={fmtVal(l.noi, "usd", true)} />
        <Fig id="recommendation" label="System" value={l.recommendation ? `${l.recommendation}${l.recommended_amount ? ` · ${fmtVal(l.recommended_amount, "usd", true)}` : ""}` : "Pending"} />
      </>}
      <Fig id="flagged-wires" label="Flagged wires" value={f.wires.flagged} tone="text-red-600" />
      <Fig id="aml-holds" label="On AML hold" value={f.wires.aml_holds} />
      <Fig id="open-grc" label="Open GRC issues" value={`${f.grc.open_issues} (${f.grc.high_open} high)`} />
      <Fig id="remediation" label="Remediation" value={`${f.grc.remediation_pct}%`} />
    </div>
  );
}

export default function Presenter() {
  const [st, setSt] = useState(null);
  const [secs, setSecs] = useState(0);
  const [ai, setAi] = useState(null);
  const [figs, setFigs] = useState(null);
  const chan = useRef(null);
  const taskId = st?.taskId;
  const aiDone = ai?.done;
  useEffect(() => {
    const load = () => api.get("/presenter/cues", { params: taskId ? { task_id: taskId } : {} }).then((r) => setFigs(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [taskId, st?.i, aiDone]);
  useEffect(() => {
    const c = new BroadcastChannel("omnifi-presenter");
    chan.current = c;
    c.onmessage = (e) => { if (e.data?.type === "state") setSt(e.data); if (e.data?.type === "ai") setAi(e.data); };
    c.postMessage({ cmd: "hello" });
    const t = setInterval(() => setSecs((x) => x + 1), 1000);
    return () => { c.close(); clearInterval(t); };
  }, []);
  const send = (cmd) => chan.current?.postMessage({ cmd });
  const mm = `${String(Math.floor(secs / 60)).padStart(2, "0")}:${String(secs % 60).padStart(2, "0")}`;
  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-8" data-testid="presenter-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <img src="/cloudscore-logo.png" alt="CloudScore" className="h-7" />
          <span className="font-display text-xl font-extrabold text-slate-900 border-l border-slate-200 pl-3">Omni<span className="text-sky-600">Fi</span></span>
          <span className="eyebrow ml-2 hidden sm:inline">Presenter notes</span>
        </div>
        <button data-testid="presenter-timer" onClick={() => setSecs(0)} className="flex items-center gap-2 num text-lg text-slate-700 border border-slate-300 rounded px-3 py-1"><Timer className="w-4 h-4" />{mm}</button>
      </div>
      {!st ? (
        <div className="panel p-10 mt-8 text-slate-600" data-testid="presenter-waiting">Waiting for the main screen… Open the <b>Guided walkthrough</b> panel in the OmniFi window and it will sync here automatically.</div>
      ) : (
        <div className="mt-8 grid lg:grid-cols-[1fr_320px] gap-6">
          <div className="panel p-8">
            <div className="num text-sm text-slate-500" data-testid="presenter-step">Step {st.i + 1} of {st.total}</div>
            <div className="h-1.5 bg-slate-200 rounded mt-2"><div className="h-full bg-sky-600 rounded transition-[width] duration-500" style={{ width: `${((st.i + 1) / st.total) * 100}%` }} /></div>
            <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900 mt-6" data-testid="presenter-title">{st.title}</h1>
            <p className="text-lg text-slate-700 mt-4 leading-relaxed" data-testid="presenter-script">{st.say}</p>
            <div className="eyebrow mt-8 mb-3">Talking points</div>
            <ul className="space-y-3" data-testid="presenter-notes">
              {(st.notes || []).map((n) => <li key={n} className="flex gap-3 text-base text-slate-800"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-sky-600 shrink-0" />{n}</li>)}
            </ul>
            <LiveAI ai={ai} />
          </div>
          <div className="space-y-4">
            <KeyFigures f={figs} />
            <div className="panel p-5">
              <div className="eyebrow mb-3">Drive the main screen</div>
              <button data-testid="presenter-run" disabled={st.busy} onClick={() => send("run")} className="w-full flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-medium py-3 rounded transition-colors disabled:opacity-60">
                {st.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}{st.busy ? "Running…" : st.cta}
              </button>
              <div className="flex gap-2 mt-2">
                <button data-testid="presenter-prev" onClick={() => send("prev")} className="flex-1 flex items-center justify-center gap-1 border border-slate-300 rounded py-2 text-sm text-slate-700"><ChevronLeft className="w-4 h-4" />Back</button>
                <button data-testid="presenter-next" onClick={() => send("next")} className="flex-1 flex items-center justify-center gap-1 border border-slate-300 rounded py-2 text-sm text-slate-700">Skip<ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="panel p-5">
              <div className="eyebrow mb-1">Up next</div>
              <div className="text-sm text-slate-800" data-testid="presenter-next-title">{st.next || "End of walkthrough"}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
